import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchJson } from '../../../../helpers';
import TileInfo from './TileInfo.jsx';
import { useDataset } from '../../hooks/useDataset.js';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import NoDataComponent from '../../no_data_component.jsx';
import { arraysHaveSameElements } from '../../DashboardGridIndex.jsx';
import StripFormatter, { calculatePresentAttributes, FILLER_TEXT } from '../base/StripFormatter.jsx';
import { IMAGE_DEFAULT_FONT, VALUE_DEFAULT_FONT, NAME_ITEM_DEFAULT_FONT } from '../../settings_sidebar/chart/ChartSettings.jsx';

import {
    monitoring_report_status_dashboard_report_phase_data_path
} from '~/routes'
import { useSelector } from 'react-redux';

const ATTRIBUTES = ['image', 'name_item', 'value'];

function WorkflowDataStripWidget({ widgetId }) {
    const queryClient = useQueryClient();
    const { data } = useDataset({widgetId});
    const dashboardId = useSelector(state => state.dashboard.dashboardId);
    const projectId = useSelector(state => state.dashboard.projectId);
    const settings = useSelector(state => state.dashboard.widgets[widgetId].settings) || {};
    const workflow_ids = useSelector(state => state.dashboard.widgets[widgetId].settings?.workflow_ids, arraysHaveSameElements);
    const all_submissions = useSelector(state => state.dashboard.widgets[widgetId].settings?.all_submissions);
    const assigned_workflows = useSelector(state => state.dashboard.widgets[widgetId].settings?.assigned_workflows);
    const own_projects = useSelector(state => state.dashboard.widgets[widgetId].settings?.own_projects);
    const stacking = useSelector(state => state.dashboard.widgets[widgetId].settings?.stacking) || 'horizontal';
    const imageFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.image_fontSize);
    const valueFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.value_fontSize);
    const nameItemFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.name_item_fontSize);

    const { datasets } = settings;
    const url = monitoring_report_status_dashboard_report_phase_data_path({
        widget_id: widgetId,
        dashboard_id: dashboardId,
        project_id: projectId,
    });
    const {data: workflow_summary, isLoading: loading} = useQuery({
        queryKey: ['workflow_summary', dashboardId, projectId, widgetId],
        queryFn: () => fetchJson(url, {method: 'POST', body: JSON.stringify({widget_settings: {settings}})}),
        enabled: !!dashboardId && !!projectId && !!widgetId && !!workflow_ids && workflow_ids.length > 0
    });
    const {workflow_summary: workflowSummary} = workflow_summary || {};
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [presentAttributes, setPresentAttributes] = useState({});
    const [localDatasets, setLocalDatasets] = useState([]);

    const getVariableValue = (source) => {
        const {total_submissions, total_processed, total_drafts, total_reviews} = workflowSummary || {}
        switch(source){
            case 'submission_count':
                return total_submissions
            case 'completed_submissions':
                return total_processed
            case 'waiver':
                return 0
            case 'workflow_completion':
                return total_submissions == 0 ? 0 + "%" : Math.floor((total_processed/total_submissions) * 100) + "%"
            case 'review_entries':
                return total_reviews
            case 'draft_entries':
                return total_drafts
            default: 
                return 0
        }
    }

    useEffect(() => {
        if (datasets.length > 0){
            const newDatasets = datasets.map(v => ({
                ...v,
                value: v.source ? getVariableValue(v.source) : (((data || {})[v.dataset_id] || [])[0] || {}).data,
            }))
            setLocalDatasets(newDatasets)
            calculatePresentAttributes(ATTRIBUTES, newDatasets, setPresentAttributes, ['name_item']);
        }
    }, [datasets, data, workflow_summary])

    useEffect(() => {
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'workflow_summary' && query.queryKey.at(-1) === widgetId});
    }, [workflow_ids, all_submissions, assigned_workflows, own_projects])

    return (
        loading ? <LoadingIndicator /> :
        (
            workflow_ids ? 
                <div style={{height: '100%', width: '100%'}}>
                    {
                        selectedDataset === null ? 
                        <div 
                            className="section-counts"
                            style={{
                                height: '100%',
                                display: stacking === 'horizontal' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gridTemplateColumns: `repeat(${localDatasets.length}, ${100/localDatasets.length}%)`
                            }}
                        >
                        {
                            localDatasets.map((v, index) => (
                                <div key={index}
                                    className="flex-1 item pointer-hover"
                                    style={{
                                        backgroundColor: v.background_color,
                                        color: v.text_color ? v.text_color : "#ffffff",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        overflow: 'hidden'
                                    }}
                                    onClick={(e) => setSelectedDataset(index)}
                                >
                                    {
                                        stacking === 'horizontal' ? 
                                        <>
                                            {Object.keys(presentAttributes).includes('image') &&
                                                <p className={"text-center widget-icon adalicon icon-" + (v.image || 'tools')}
                                                    style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`, marginTop: '1rem', color: v.image ? v.text_color : v.background_color}}
                                                ></p>
                                            }

                                            {Object.keys(presentAttributes).includes('value') &&
                                                <StripFormatter
                                                presentAttributes={presentAttributes}
                                                classNames="qty numscroller"
                                                fontVar={valueFontSize}
                                                str={v.value || FILLER_TEXT}
                                                color={v.background_color}
                                                defaultFont={VALUE_DEFAULT_FONT}
                                                attrKey='value'
                                            />}
                                            {Object.keys(presentAttributes).includes('name_item') &&
                                            <StripFormatter
                                                presentAttributes={presentAttributes}
                                                classNames='name'
                                                fontVar={nameItemFontSize}
                                                str={v.name_item}
                                                color={v.background_color}
                                                defaultFont={NAME_ITEM_DEFAULT_FONT}
                                                attrKey='name_item'
                                            />
                                            }
                                        </>   
                                        :
                                        <>
                                            {v.image && <p className={"text-center widget-icon adalicon icon-" + v.image} style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`}}></p>}
                                            {v.value && <p className="qty numscroller mt-0" style={{"--font-size": `${valueFontSize || VALUE_DEFAULT_FONT}px`}}>{v.value}</p>}
                                            {v.name_item && <p className="name mt-0" style={{"--font-size": `${nameItemFontSize || NAME_ITEM_DEFAULT_FONT}px`}}>{v.name_item}</p>}
                                        </>
                                    }
                                </div>))
                        }
                        </div>
                        :
                        <TileInfo dataset={{
                        ...datasets[selectedDataset],
                        widget_id: widgetId, 
                        dashboard_id: dashboardId,
                        project_id: projectId}} 
                        settings={settings}
                        backClick={() => {setSelectedDataset(null)}} 
                        />
                    }
                </div>
            : <NoDataComponent />
        )
    )

}

export default WorkflowDataStripWidget;
